import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Warning } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Panel, PanelHeading } from './ui/panel';
import { useAuth } from '../contexts/AuthContext';
import { accessErrorMessage } from '../lib/accessCopy';
// The transparent crest master, not a crop of the mockup (03_APP_LOCK).
import crest from '../assets/crest.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Long enough to copy something out of another app, short enough that a
// stolen phone is not simply waited out.
const GRACE_MS = 60 * 1000;

const AWAY_KEY = 'arca.lock.awaySince';
const PREF_KEY = 'arca.lock.enabled';

/** Per-device, on unless the member turned it off on this device. A screen
 *  lock is a property of the phone in your pocket, not of the account. */
export function isAppLockEnabled() {
  try {
    return localStorage.getItem(PREF_KEY) !== 'off';
  } catch (e) {
    return true;
  }
}

export function setAppLockEnabled(on) {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch (e) { /* private mode — the lock simply stays at its default */ }
}

/**
 * Locks the app behind a password when it has been away from the foreground,
 * and blanks the screen the moment it goes to the background.
 *
 * The blanking matters as much as the lock: when an app is backgrounded the
 * phone screenshots it for the task switcher, and that screenshot would
 * otherwise show the conversation to anyone thumbing through open apps.
 *
 * Client-side, so it is friction rather than cryptography — it stops someone
 * holding an unlocked phone, not someone with the device and time to spare.
 */
const AppLock = () => {
  const { user, sessionOrigin } = useAuth();
  const navigate = useNavigate();
  const signedIn = !!user && user !== false;

  const [locked, setLocked] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const wasSignedIn = useRef(false);

  const awaySince = () => {
    try {
      const v = localStorage.getItem(AWAY_KEY);
      return v ? parseInt(v, 10) : null;
    } catch (e) {
      return null;
    }
  };
  const markAway = () => {
    try { localStorage.setItem(AWAY_KEY, String(Date.now())); } catch (e) { /* ignore */ }
  };
  const clearAway = () => {
    try { localStorage.removeItem(AWAY_KEY); } catch (e) { /* ignore */ }
  };

  // What opens the lock is proof given in THIS page load — not the existence
  // of a user object.
  //
  // This used to watch `user` alone, and both a typed password and a cookie
  // the browser still had end in the same setUser(data). So "he has just
  // signed in" was the reading in both cases, the away stamp was cleared, and
  // the effect below found nothing to act on. A browser closed and reopened
  // five minutes later walked straight in — past the lock, and past the
  // disguise, which is the whole thing they exist for. Confirmed on Windows
  // and Android before it was fixed.
  useEffect(() => {
    if (!signedIn) {
      wasSignedIn.current = false;
      setLocked(false);
      return;
    }
    if (wasSignedIn.current) return;   // decided once per session, not per render
    wasSignedIn.current = true;

    if (sessionOrigin === 'fresh') {
      clearAway();                     // a password was typed to get here
      setLocked(false);
      return;
    }
    if (!isAppLockEnabled()) return;

    // Restored from a cookie, or not yet known — and unknown is treated as
    // restored, because the safe reading is that nothing was proved here.
    //
    // A reload is not an absence: the page going away stamps the clock, so
    // coming straight back reads as seconds and costs nothing. Anything
    // longer is an absence. So is a browser killed hard enough to leave no
    // stamp at all, which is why a missing stamp locks rather than opens.
    const t = awaySince();
    if (!t || Date.now() - t > GRACE_MS) setLocked(true);
  }, [signedIn, sessionOrigin]);

  useEffect(() => {
    if (!signedIn) return undefined;

    // Derived from document.hidden rather than accumulated, and driven by
    // three signals rather than one. An earlier version only listened for
    // visibilitychange and only ever cleared the blank sheet from that
    // handler — so when a phone froze the page or restored it from cache and
    // the event never arrived, the sheet stayed up and the app was simply a
    // black screen. Blanking may be missed harmlessly; failing to UN-blank
    // locks the member out of their own app.
    const sync = () => {
      if (document.hidden) {
        setHidden(true);          // blank before the OS snapshots the app
        markAway();
        return;
      }
      setHidden(false);
      if (!isAppLockEnabled()) return;
      const t = awaySince();
      if (t && Date.now() - t > GRACE_MS) setLocked(true);
      else clearAway();
    };

    // pagehide is the one a phone sends reliably when the page goes away for
    // good, and it is what makes a reload distinguishable from an absence:
    // without a stamp on the way out, every reopen looks like a cold start.
    const leaving = () => markAway();

    document.addEventListener('visibilitychange', sync);
    window.addEventListener('pageshow', sync);   // restored from back/forward cache
    window.addEventListener('focus', sync);      // resumed without a visibility event
    window.addEventListener('pagehide', leaving);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('pagehide', leaving);
    };
  }, [signedIn]);

  const unlock = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/reauth`, { password }, { withCredentials: true });
      setPassword('');
      clearAway();
      setLocked(false);
    } catch (err) {
      // A session that has really ended never lands here: the refresh
      // interceptor signs the member out, and this sheet gives way to the gate.
      setError(accessErrorMessage(
        { status: err.response?.status || 0, error: err.response?.data?.detail },
        'Could not confirm that password.',
      ));
    } finally {
      setBusy(false);
    }
  };

  if (!signedIn) return null;

  // Backgrounded but not (yet) locked: an opaque sheet with nothing on it.
  // The document.hidden check is the last line of defence — if state and
  // reality ever disagree, reality wins and the member sees their app.
  if (hidden && !locked && typeof document !== 'undefined' && document.hidden) {
    return (
      <div
        className="fixed inset-0 z-[100] bg-background"
        aria-hidden="true"
        data-testid="app-blank"
      />
    );
  }

  if (!locked) return null;

  // Fully opaque and deliberately anonymous: no route, no name, no preview of
  // what is underneath -- only the crest, which says nothing about who you are.
  return (
    <div
      className="fixed inset-0 z-[100] bg-background overflow-y-auto flex flex-col items-center justify-center px-4 py-8"
      data-testid="app-lock"
    >
      <img
        src={crest}
        alt=""
        aria-hidden="true"
        className="w-24 h-auto mb-8 opacity-90 select-none"
        draggable={false}
      />
      <Panel as="form" onSubmit={unlock} className="w-full max-w-sm p-5">
        <div className="text-center pb-3 mb-4 border-b border-border">
          <PanelHeading>Arcanum is locked</PanelHeading>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your password to continue
          </p>
        </div>

        {/* Password managers only offer to fill a password when a username
            field sits alongside it. Visually hidden rather than display:none,
            which many managers skip over. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={user?.email || ''}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="absolute opacity-0 pointer-events-none h-0 w-0"
        />

        <div className="space-y-1.5">
          <Label htmlFor="app-lock-password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Password
          </Label>
          <Input
            id="app-lock-password"
            name="password"
            type="password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            placeholder="Your password"
            autoComplete="current-password"
            autoFocus
            required
            className="h-11"
            data-testid="app-lock-password"
          />
        </div>

        {error && (
          <div role="alert" className="flex items-start gap-2 text-sm text-destructive mt-3">
            <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" aria-hidden="true" />
            <span data-testid="app-lock-error">{error}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={busy || !password}
          className="w-full h-11 mt-4 bg-primary hover:bg-primary/90 font-semibold tracking-wide"
          data-testid="app-lock-submit"
        >
          {busy ? 'Unlocking…' : 'Unlock'}
        </Button>

        {/* The way out for someone who cannot, or should not, unlock:
            Farewell signs out properly before it returns to the gate. */}
        <button
          type="button"
          onClick={() => navigate('/farewell', { replace: true })}
          className="w-full min-h-[44px] mt-2 -mb-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="app-lock-signout"
        >
          Sign out and return to the gate
        </button>
      </Panel>
    </div>
  );
};

export default AppLock;
